import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BackHomeButton from '../components/BackHomeButton';
import { useTranslation } from 'react-i18next';
import { setItemJSON } from '../utils/storage';
import './UnifiedLogin.css';
import { useEffect } from 'react';

function UnifiedLogin({ setUser }) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	// When on the login page, pressing the browser Back button should go to Home
	useEffect(() => {
		const onPop = (e) => {
			// Redirect back to home instead of navigating to previous protected pages
			navigate('/', { replace: true });
		};

		window.addEventListener('popstate', onPop);
		return () => window.removeEventListener('popstate', onPop);
	}, [navigate]);
	
	const [userType, setUserType] = useState('candidate');
	const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'otp'
	const [formData, setFormData] = useState({
		emailOrMobile: '',
		password: ''
	});
	
	// OTP related state
	const [otpData, setOtpData] = useState({
		phoneNumber: '',
		otp: '',
		isOtpSent: false,
		isVerifying: false,
		countdown: 0
	});
	
	const [errors, setErrors] = useState({});
	const [isLoggingIn, setIsLoggingIn] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showForgotModal, setShowForgotModal] = useState(false);
	const [copied, setCopied] = useState(false);
	// Auth feedback modal state (replace alert() usage)
	const [authModal, setAuthModal] = useState({ open: false, title: '', message: '', onClose: null });

	const showAuthModal = (title, message, onClose = null) => {
		setAuthModal({ open: true, title: title || 'Notice', message: message || '', onClose });
	};

	const closeAuthModal = () => {
		const cb = authModal && authModal.onClose;
		setAuthModal({ open: false, title: '', message: '', onClose: null });
		if (typeof cb === 'function') cb();
	};
	// ...existing state

	const handleUserTypeChange = (e) => {
		setUserType(e.target.value);
		setFormData({ emailOrMobile: '', password: '' });
		setLoginMethod('password');
		setOtpData({ phoneNumber: '', otp: '', isOtpSent: false, isVerifying: false, countdown: 0 });
		setErrors({});
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		
		// Auto-detect if input is mobile number or email for candidates
		if (userType === 'candidate' && name === 'emailOrMobile') {
			const cleanValue = value.trim();
			// Check if it's a mobile number (digits only, optionally with +91 or 91 prefix)
			const mobileRegex = /^(\+91|91)?[6-9]\d{0,9}$/;
			const emailRegex = /@/; // Simple check for @ symbol
			
			if (mobileRegex.test(cleanValue.replace(/\s+/g, ''))) {
				// It's a mobile number - switch to OTP login
				setLoginMethod('otp');
				setOtpData(prev => ({ ...prev, phoneNumber: cleanValue }));
			} else if (emailRegex.test(cleanValue)) {
				// It's an email - switch to password login
				setLoginMethod('password');
			}
		}
		
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const handleOtpInputChange = (e) => {
		const { name, value } = e.target;
		setOtpData(prev => ({
			...prev,
			[name]: value
		}));
		
		if (errors[name]) {
			setErrors(prev => ({
				...prev,
				[name]: ''
			}));
		}
	};

	// Send OTP function
	const sendOTP = async () => {
		try {
			// Use the unified input field value
			const phoneNumber = formData.emailOrMobile || otpData.phoneNumber;
			
			if (!phoneNumber.trim()) {
				setErrors({ emailOrMobile: 'Mobile number is required' });
				return;
			}

			// Validate phone number format
			const phoneRegex = /^(\+91|91)?[6-9]\d{9}$/;
			const cleanPhone = phoneNumber.replace(/\s+/g, '').replace(/^\+/, '');
			
			if (!phoneRegex.test(cleanPhone)) {
				setErrors({ emailOrMobile: 'Please enter a valid Indian mobile number (10 digits starting with 6-9)' });
				return;
			}

			setIsLoggingIn(true);
			const response = await fetch('https://tstranscareers.in/api/send-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					phoneNumber: phoneNumber
				})
			});

			const data = await response.json();

			if (response.ok && data.success) {
				setOtpData(prev => ({ ...prev, phoneNumber: phoneNumber, isOtpSent: true, countdown: 300 })); // 5 minutes countdown
				showAuthModal('OTP sent', 'OTP sent successfully to your registered mobile number');
				
				// Start countdown
				const timer = setInterval(() => {
					setOtpData(prev => {
						if (prev.countdown <= 1) {
							clearInterval(timer);
							return { ...prev, countdown: 0, isOtpSent: false };
						}
						return { ...prev, countdown: prev.countdown - 1 };
					});
				}, 1000);
			} else {
				if (response.status === 404) {
					setErrors({ emailOrMobile: 'No account found with this mobile number. Please register first.' });
				} else if (response.status === 403) {
					setErrors({ emailOrMobile: 'Your account is not approved yet. Please contact TG Helpline: 155326' });
				} else {
					setErrors({ emailOrMobile: data.message || 'Failed to send OTP' });
				}
			}
		} catch (error) {
			console.error('Send OTP error:', error);
			showAuthModal('Network error', 'Please try again.');
		} finally {
			setIsLoggingIn(false);
		}
	};

	// Verify OTP function
	const verifyOTP = async () => {
		try {
			if (!otpData.otp.trim()) {
				setErrors({ otp: 'OTP is required' });
				return;
			}

			setOtpData(prev => ({ ...prev, isVerifying: true }));
			const response = await fetch('https://tstranscareers.in/api/verify-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					phoneNumber: otpData.phoneNumber,
					otp: otpData.otp
				})
			});

			const data = await response.json();

			if (response.ok && data.success) {
				// Store user data in sessionStorage
				setItemJSON('candidateLoggedIn', data.user);
				setUser(data.user);
				
				// Fetch profile data
				try {
					const profileRes = await fetch(`https://tstranscareers.in/api/candidate-profile/${data.user.id}`);
					const profileData = await profileRes.json();
					setItemJSON('candidateData', profileData);
				} catch (profileError) {
					console.error('Error fetching profile:', profileError);
				}

				showAuthModal('Login successful!', 'Welcome back, ' + data.user.candidateName, () => navigate('/candidate-dashboard'));
			} else {
				showAuthModal('Login failed', data.message || 'Invalid OTP');
			}
		} catch (error) {
			console.error('Verify OTP error:', error);
			showAuthModal('Network error', 'Please try again.');
		} finally {
			setOtpData(prev => ({ ...prev, isVerifying: false }));
		}
	};

	const formatCountdown = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	const validateForm = () => {
		const newErrors = {};

		if (userType === 'candidate') {
			if (loginMethod === 'otp') {
				// OTP login validation
				if (!otpData.phoneNumber.trim()) {
					newErrors.phoneNumber = 'Phone number is required';
				}
				if (otpData.isOtpSent && !otpData.otp.trim()) {
					newErrors.otp = 'OTP is required';
				}
				if (otpData.isOtpSent && otpData.otp.length !== 6) {
					newErrors.otp = 'OTP must be 6 digits';
				}
			} else {
				// Password login validation
				if (!formData.emailOrMobile.trim()) {
					newErrors.emailOrMobile = '';
				}
				if (!formData.password) {
					newErrors.password = '';
				}
			}
		} else {
			// Non-candidate validation (admin/employer)
			if (!formData.emailOrMobile.trim()) {
				newErrors.emailOrMobile = '';
			} else if (!/\S+@\S+\.\S+/.test(formData.emailOrMobile)) {
				newErrors.emailOrMobile = 'Please enter a valid email address';
			}
			if (!formData.password) {
				newErrors.password = '';
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (!validateForm()) {
			return;
		}

		setIsLoggingIn(true);
		
		try {
			if (userType === 'candidate') {
				let response, data;

				if (loginMethod === 'otp') {
					// OTP verification - use the unified input field
					const phoneNumber = formData.emailOrMobile || otpData.phoneNumber;
					response = await fetch('https://tstranscareers.in/api/verify-otp', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							phoneNumber: phoneNumber,
							otp: otpData.otp
						})
					});
					data = await response.json();
				} else {
					// Password login
					response = await fetch('https://tstranscareers.in/api/login-candidate', {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({
							emailOrMobile: formData.emailOrMobile,
							password: formData.password
						})
					});
					data = await response.json();
				}

				if (response.ok) {
					// Check approval status first
					const approvalStatus = data.user.approval_status || 'pending';
					
					if (approvalStatus === 'approved') {
						// Store user data in sessionStorage (expires when browser closes)
						setItemJSON('candidateLoggedIn', data.user);
						setUser(data.user);
						const profileRes = await fetch(`https://tstranscareers.in/api/candidate-profile/${data.user.id}`);
						const profileData = await profileRes.json();
						setItemJSON('candidateData', profileData);

						showAuthModal('Login successful!', 'Welcome back, ' + data.user.name, () => navigate('/candidate-dashboard'));
					} else if (approvalStatus === 'pending') {
						showAuthModal('Pending approval', 'Your profile is in pending status. Once it is approved by admin, you can access your dashboard. Please wait for approval.');
					} else if (approvalStatus === 'denied') {
						showAuthModal('Profile rejected', 'Sorry, your profile has been rejected. For any query, please contact TG helpline: 155326');
					} else {
						showAuthModal('Under review', 'Your profile is under review. Please wait for admin approval.');
					}
				} else {
					showAuthModal('Login failed', data.message || 'Please try again.');
				}
			} else if (userType === 'employer') {
				const response = await fetch('https://tstranscareers.in/api/login-employer', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						emailOrMobile: formData.emailOrMobile,
						password: formData.password
					})
				});

				const data = await response.json();

				if (response.ok) {
					// Check approval status first - backend sends approvalStatus in camelCase
					const approvalStatus = data.user.approvalStatus || data.user.approval_status || 'pending';
					
					if (approvalStatus === 'approved') {
						// Store user data in sessionStorage (expires when browser closes)
						setItemJSON('employerLoggedIn', data.user);
						setUser(data.user);

						// Also store in localStorage for dashboard profile access
						localStorage.setItem('employerData', JSON.stringify(data.user));

						showAuthModal('Login successful!', 'Welcome back, ' + data.user.name, () => navigate('/employer-dashboard'));
					} else if (approvalStatus === 'pending') {
						showAuthModal('Pending approval', 'Your profile is in pending status. Once it is approved by admin, you can access your dashboard. Please wait for approval.');
					} else if (approvalStatus === 'denied') {
						showAuthModal('Profile rejected', 'Sorry, your profile has been rejected. For any query, please contact TG helpline: 155326');
					} else {
						showAuthModal('Under review', 'Your profile is under review. Please wait for admin approval.');
					}
				} else {
					showAuthModal('Login failed', data.message || 'Please try again.');
				}
			} else if (userType === 'admin') {
  const response = await fetch('https://tstranscareers.in/api/login-admin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: formData.emailOrMobile,
      password: formData.password
    })
  });
  const data = await response.json();
	if (response.ok) {
		// Store both token and user so frontend can send Authorization: Bearer <token>
		// Use sessionStorage to ensure session expires when browser closes
		setItemJSON('adminLoggedIn', { token: data.token, ...data.user });
		setUser(data.user);
		showAuthModal('Admin login successful!', '', () => navigate('/admin-dashboard'));
	} else {
		showAuthModal('Invalid credentials', data.message || 'Invalid admin credentials.');
	}
}
			
		} catch (error) {
			console.error('Login error:', error);
			showAuthModal('Login failed', 'Please check your connection and try again.');
		} finally {
			setIsLoggingIn(false);
		}
	};

	const getPlaceholder = () => {
		// For candidates we use the unified email-or-mobile placeholder.
		// For employer/admin explicitly show the registered email placeholder to match design.
		if (userType === 'candidate') return t('login.enterEmailOrMobile');
		return 'Enter your email address';
	};

	const getInputType = () => {
		return userType === 'candidate' ? 'text' : 'email';
	};

	const getLabel = () => {
		return userType === 'candidate' 
			? t('login.emailOrMobile')
			: t('login.email');
	};

  const shouldShowRegistration = () => userType !== 'admin';
  const getRegistrationLink = () => userType === 'candidate' ? '/candidate-registration' : '/employer-registration';
  const getRegistrationText = () => userType === 'candidate' ? t('login.registerCandidate') : t('login.registerEmployer');
  
  // Function to handle registration navigation
  const handleRegistrationClick = (e) => {
    e.preventDefault();
    const path = getRegistrationLink();
    navigate(path);
    window.scrollTo(0, 0);
  };
	return (
		<>
		<div className="unified-login">
			<div className="container">
								<BackHomeButton />
				<div className="login-header">
					<h1>{t('login.login')}</h1>
					<p>{t('login.welcomeBack')}</p>
				</div>

				<div className="login-form-container">
					
					<form onSubmit={handleSubmit} className="login-form">
						<div className="form-group">
							<label>{t('login.loginAs')}</label>
									<div className="login-options">
										<div className="login-option-box">
											<input
												type="radio"
												id="candidate"
												name="userType"
												value="candidate"
												checked={userType === 'candidate'}
												onChange={handleUserTypeChange}
											/>
											<label htmlFor="candidate">{t('login.candidate')}</label>
										</div>
										<div className="login-option-box">
											<input
												type="radio"
												id="employer"
												name="userType"
												value="employer"
												checked={userType === 'employer'}
												onChange={handleUserTypeChange}
											/>
											<label htmlFor="employer">{t('login.employer')}</label>
										</div>
										<div className="login-option-box">
											<input
												type="radio"
												id="admin"
												name="userType"
												value="admin"
												checked={userType === 'admin'}
												onChange={handleUserTypeChange}
											/>
											<label htmlFor="admin">{t('login.admin')}</label>
										</div>
								</div>
						</div>

						{/* Login Method Selection for Candidates - Now Auto-Detected */}
						{/* {userType === 'candidate' && (
							<div className="form-group">
								<label>Choose Login Method</label>
								<div className="login-method-options">
									<div className="login-method-box">
										<input
											type="radio"
											id="password-login"
											name="loginMethod"
											value="password"
											checked={loginMethod === 'password'}
											onChange={(e) => setLoginMethod(e.target.value)}
										/>
										<label htmlFor="password-login">Password Login (Email)</label>
									</div>
									<div className="login-method-box">
										<input
											type="radio"
											id="otp-login"
											name="loginMethod"
											value="otp"
											checked={loginMethod === 'otp'}
											onChange={(e) => setLoginMethod(e.target.value)}
										/>
										<label htmlFor="otp-login">OTP Login (Mobile)</label>
									</div>
								</div>
								<p className="help-text" style={{fontSize: '0.85rem', color: '#666', marginTop: '0.5rem'}}>
									💡 Tip: Login method will auto-switch based on your input (email → password, mobile → OTP)
								</p>
							</div>
						)} */}

						{/* Unified Input Field for Candidates - Auto-detects email or mobile */}
						{userType === 'candidate' && (
							<>
								<div className="form-group">
									<label htmlFor="emailOrMobile" className="required">
										{loginMethod === 'password' ? 'Email Address' : 'Mobile Number'}
									</label>
									<input
										type="text"
										id="emailOrMobile"
										name="emailOrMobile"
										value={formData.emailOrMobile}
										onChange={handleInputChange}
										placeholder={loginMethod === 'password' 
											? 'Enter your email address' 
											: 'Enter your mobile number (10 digits)'}
										autoComplete="username"
										className={errors.emailOrMobile ? 'input-error' : ''}
									/>
									{errors.emailOrMobile && (
										<span className="error">{errors.emailOrMobile || 'This field is required'}</span>
									)}
								</div>

								{/* Show Password Field for Email Login */}
								{loginMethod === 'password' && (
									<div className="form-group">
										<label htmlFor="password" className="required">{t('login.password')}</label>
										<div className="password-input">
											<input
												type={showPassword ? "text" : "password"}
												id="password"
												name="password"
												value={formData.password}
												onChange={handleInputChange}
												placeholder={t('login.enterPassword')}
												autoComplete="current-password"
												className={errors.password ? 'input-error' : ''}
											/>
											<button
												type="button"
												className="password-toggle"
												onClick={() => setShowPassword(!showPassword)}
											>
												<i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
											</button>
										</div>
										{errors.password && <span className="error">{errors.password}</span>}
									</div>
								)}

								{/* Show OTP Fields for Mobile Login */}
								{loginMethod === 'otp' && (
									<>
										{!otpData.isOtpSent ? (
											<button
												type="button"
												className="send-otp-btn"
												onClick={() => {
													// Sync the mobile number from emailOrMobile to otpData
													setOtpData(prev => ({ ...prev, phoneNumber: formData.emailOrMobile }));
													setTimeout(sendOTP, 100);
												}}
												disabled={isLoggingIn || !formData.emailOrMobile.trim()}
											>
												{isLoggingIn ? 'Sending...' : 'Send OTP'}
											</button>
										) : (
											<div className="otp-sent-info">
												<p>✅ OTP sent successfully to {formData.emailOrMobile}</p>
												{otpData.countdown > 0 && (
													<p className="countdown">Resend OTP in: {formatCountdown(otpData.countdown)}</p>
												)}
												{otpData.countdown === 0 && (
													<button
														type="button"
														className="resend-otp-btn"
														onClick={() => {
															setOtpData(prev => ({ ...prev, phoneNumber: formData.emailOrMobile }));
															setTimeout(sendOTP, 100);
														}}
														disabled={isLoggingIn}
													>
														Resend OTP
													</button>
												)}
											</div>
										)}

										{otpData.isOtpSent && (
											<div className="form-group">
												<label htmlFor="otp" className="required">Enter OTP</label>
												<input
													type="text"
													id="otp"
													name="otp"
													value={otpData.otp}
													onChange={handleOtpInputChange}
													placeholder="Enter 6-digit OTP"
													maxLength="6"
													className={errors.otp ? 'input-error' : ''}
												/>
												{errors.otp && <span className="error">{errors.otp}</span>}
											</div>
										)}
									</>
								)}
							</>
						)}

						{/* Non-candidate login fields (original) */}
						{userType !== 'candidate' && (
							<>
								<div className="form-group">
									<label htmlFor="emailOrMobile" className="required">{getLabel()}</label>
											<input
												type={getInputType()}
												id="emailOrMobile"
												name="emailOrMobile"
												value={formData.emailOrMobile}
												onChange={handleInputChange}
												placeholder={getPlaceholder()}
												autoComplete="username"
												className={errors.emailOrMobile ? 'input-error' : ''}
											/>
											{errors.emailOrMobile && (
												<span className="error">{errors.emailOrMobile || 'This field is required'}</span>
											)}
								</div>

								<div className="form-group">
									<label htmlFor="password" className="required">{t('login.password')}</label>
									<div className="password-input">
										<input
											type={showPassword ? "text" : "password"}
											id="password"
											name="password"
											value={formData.password}
											onChange={handleInputChange}
											placeholder={t('login.enterPassword')}
											autoComplete="current-password"
											className={errors.password ? 'input-error' : ''}
										/>
										<button
											type="button"
											className="password-toggle"
											onClick={() => setShowPassword(!showPassword)}
										>
											<i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
										</button>
									</div>
									{errors.password && <span className="error">{errors.password}</span>}
								</div>
							</>
						)}

						<button 
							type="submit" 
							className="login-btn"
							disabled={isLoggingIn || (loginMethod === 'otp' && !otpData.isOtpSent)}
						>
							{isLoggingIn 
								? (loginMethod === 'otp' ? 'Verifying OTP...' : t('login.loggingIn'))
								: (loginMethod === 'otp' ? 'Verify OTP' : t('login.login'))
							}
						</button>

						<div className="form-footer">
							<div className="forgot-password">
								<button type="button" className="link-button" onClick={() => setShowForgotModal(true)}>{t('login.forgotPassword')}</button>
							</div>
							
							{shouldShowRegistration() && (
								<div className="register-link">
									<p>{t('login.dontHaveAccount')} <Link to={getRegistrationLink()}>{getRegistrationText()}</Link></p>
								</div>
							)}
						</div>
					</form>

					{/* Forgot password centered modal */}
					{showForgotModal && (
						<div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Forgot Password Help">
							<div className="modal-panel">
								<h2>{t('login.forgotPassword')}</h2>
								<p>Please contact our helpline to change your password.</p>
								<p className="helpline-number">155326</p>
								<div className="modal-actions">
									<button className="btn btn-secondary" onClick={async () => {
										try {
											await navigator.clipboard.writeText('155326');
											setCopied(true);
											setTimeout(() => setCopied(false), 2000);
										} catch (err) {
											console.warn('Clipboard copy failed', err);
											showAuthModal('Helpline', '155326');
										}
									}}>{copied ? 'Copied' : 'Copy'}</button>
									<button className="btn btn-primary" onClick={() => setShowForgotModal(false)}>Close</button>
								</div>
							</div>
						</div>
					)}

						{/* Auth feedback modal (replaces alert()) */}
						{authModal.open && (
							<div className="modal-overlay" role="dialog" aria-modal="true" aria-label={authModal.title || 'Message'}>
								<div className="modal-panel">
									<h2>{authModal.title}</h2>
									{authModal.message && <p>{authModal.message}</p>}
									<div className="modal-actions">
										<button className="btn btn-primary" onClick={closeAuthModal}>{'OK'}</button>
									</div>
								</div>
							</div>
						)}

					

					<div className="login-info">
						<h3>{t('login.loginInstructions')}</h3>
						<ul>
							<li>{t('login.selectUserType')}</li>
							<li>{t('login.useRegistered')}</li>
							<li>{t('login.enterCreatedPassword')}</li>
							<li>{t('login.ensureDevice')}</li>
							<li>{t('login.contactSupport')}</li>
						</ul>
						
						{userType === 'employer' && (
							<div className="features">
								<h4>{t('login.orgBenefits')}</h4>
								<ul>
									<li>{t('login.benefit1')}</li>
									<li>{t('login.benefit2')}</li>
									<li>{t('login.benefit3')}</li>
									<li>{t('login.benefit4')}</li>
								</ul>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
		</>
	);
}

export default UnifiedLogin;
